import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { WizardStepModel, WizardStepNavProps } from "./wizardSteps";
import type { WizardStepPath } from "./wizardStepPaths";

interface Args<K extends string, V> {
  model: WizardStepModel<K, V>;
  visibility: V;
  stepPaths: Record<K, WizardStepPath>;
  stepLabel: (step: K) => string;
  beforeNavigate?: () => Promise<boolean>;
}

interface WizardStepNavigation<K extends string> {
  currentStep: K | null;
  navigateToStep: (step: K) => void;
  navPropsFor: (step: K) => WizardStepNavProps;
}

export const useWizardStepNavigation = <K extends string, V>({
  model,
  visibility,
  stepPaths,
  stepLabel,
  beforeNavigate,
}: Args<K, V>): WizardStepNavigation<K> => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const visibleKeys = model.visibleKeys(visibility);
  const rawStep = searchParams.get("step");
  const currentStep = visibleKeys.includes(rawStep as K) ? (rawStep as K) : null;

  const firstStep = visibleKeys.length > 0 ? stepPaths[visibleKeys[0]] : undefined;
  const firstPathname = firstStep?.pathname;
  const firstSearch = firstStep?.search;
  useEffect(() => {
    if (currentStep || firstPathname === undefined) return;
    navigate({ pathname: firstPathname, search: firstSearch }, { replace: true });
  }, [currentStep, firstPathname, firstSearch, navigate]);

  const navigateToStep = (step: K) => {
    navigate(stepPaths[step]);
    window.scrollTo(0, 0);
  };

  const goTo = (step: K | undefined): (() => void) | undefined => {
    if (!step) return undefined;
    if (!beforeNavigate) return () => navigateToStep(step);
    return async () => {
      if (await beforeNavigate().catch(() => false)) navigateToStep(step);
    };
  };

  const navPropsFor = (step: K): WizardStepNavProps => {
    const { previous, next } = model.neighbors(step, visibility);
    return {
      previousStep: goTo(previous),
      nextStep: goTo(next),
      nextStepLabel: next ? stepLabel(next) : undefined,
    };
  };

  return { currentStep, navigateToStep, navPropsFor };
};
